import EntityStatus from "./EntityStatus";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Employee,
  formatEmployeeLocationType,
  getEmployeeClientCompany,
} from "../types/employees";
import EmployeeDetailsModal from "./EmployeeDetailsModal";
import GenericTable, { GenericTableColumn } from "./GenericTable";

type EmployeesTableProps = {
  employees: Employee[];
  canMaintainEmployees: boolean;
};

function EmployeesTable({
  employees,
  canMaintainEmployees,
}: EmployeesTableProps) {
  const { t } = useTranslation();
  const [viewingEmployee, setViewingEmployee] = useState<Employee | null>(null);

  const columns: GenericTableColumn<Employee>[] = [
    {
      id: "fullName",
      header: "Full Name",
      cell: (employee) => (
        <>
          {employee.fullName} <EntityStatus deletedAt={employee.deletedAt} />
        </>
      ),
    },
    {
      id: "nationalId",
      header: "National ID",
      cell: (employee) => employee.nationalId,
    },
    {
      id: "clientCompany",
      header: "Client Company",
      cell: (employee) => (
        <>
          {getEmployeeClientCompany(employee)?.name ?? "-"}{" "}
          <EntityStatus
            deletedAt={getEmployeeClientCompany(employee)?.deletedAt}
          />
        </>
      ),
    },
    {
      id: "workstation",
      header: "Workstation",
      cell: (employee) => (
        <>
          {employee.workstation?.name ?? "-"}{" "}
          <EntityStatus deletedAt={employee.workstation?.deletedAt} />
        </>
      ),
    },
    {
      id: "locationType",
      header: t("Location"),
      cell: (employee) => formatEmployeeLocationType(employee.locationType),
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "actionsColumn",
      cellClassName: "actionsColumn",
      cell: (employee) => (
        <div className="headerActions">
          <button
            className="secondaryButton tableActionButton"
            type="button"
            onClick={() => setViewingEmployee(employee)}
          >
            {t("View")}
          </button>

          {canMaintainEmployees ? (
            <Link
              className="secondaryButton tableActionButton"
              to={`/employees/${employee.id}/edit`}
            >
              Edit
            </Link>
          ) : (
            <span className="secondaryButton tableActionButton disabledAction">
              Edit
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <GenericTable
        ariaLabel="Employees"
        columns={columns}
        rows={employees}
        rowKey={(employee) => employee.id}
      />

      <EmployeeDetailsModal
        employee={viewingEmployee}
        isOpen={viewingEmployee !== null}
        onClose={() => setViewingEmployee(null)}
      />
    </>
  );
}

export default EmployeesTable;
